import { useState, useEffect } from "react";
import {
  X, ChevronLeft, ChevronRight, AlertTriangle, Paperclip,
  FileText, Activity, CheckSquare, Users, Download,
  Calendar, AlignLeft, MessageSquare, Loader
} from "lucide-react";
import axiosInstance from "../../API/interceptor";


/* ══ HELPERS ══ */
const fmtDate = (v) => {
  if (!v) return "—";
  if (typeof v === "object") return "—";
  const dt = new Date(v);
  if (isNaN(dt)) return String(v);
  const dd   = String(dt.getDate()).padStart(2, "0");
  const mm   = String(dt.getMonth() + 1).padStart(2, "0");
  const h24  = dt.getHours();
  const h12  = h24 % 12 || 12;
  const ampm = h24 >= 12 ? "PM" : "AM";
  return `${dd}-${mm}-${dt.getFullYear()} ${String(h12).padStart(2,"0")}:${String(dt.getMinutes()).padStart(2,"0")} ${ampm}`;
};

const colorMap  = ["#6366F1","#8B5CF6","#EC4899","#F59E0B","#10B981","#3B82F6"];
const initColor = (n = "") => colorMap[(n || "").charCodeAt(0) % colorMap.length];

function Av({ name = "?", size = 28 }) {
  const ini = (name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <span className="inline-flex items-center justify-center rounded-full text-white font-semibold shrink-0"
      style={{ width:size, height:size, fontSize:size*0.35, background:initColor(name) }}>{ini}</span>
  );
}

const STATUS_CFG = {
  "open":             { label:"Open",           bg:"#EEEDFE", color:"#3C3489", dot:"#534AB7" },
  "in progress":      { label:"In Progress",    bg:"#D1FAE5", color:"#065F46", dot:"#10B981" },
  "in-progress":      { label:"In Progress",    bg:"#D1FAE5", color:"#065F46", dot:"#10B981" },
  "on hold":          { label:"On Hold",        bg:"#FCE7F3", color:"#9D174D", dot:"#EC4899" },
  "on-hold":          { label:"On Hold",        bg:"#FCE7F3", color:"#9D174D", dot:"#EC4899" },
  "completed":        { label:"Complete",       bg:"#D1FAE5", color:"#065F46", dot:"#10B981" },
  "approval pending": { label:"Approval Await", bg:"#FEF3C7", color:"#92400E", dot:"#F59E0B" },
  "closed":           { label:"Closed",         bg:"#F3F4F6", color:"#374151", dot:"#6B7280" },
  "pending":          { label:"Pending",        bg:"#F1F5F9", color:"#475569", dot:"#94A3B8" },
};

const PRIORITY_CFG = {
  "URGENT": { bg:"#FEE2E2", color:"#991B1B" },
  "HIGH":   { bg:"#FFEDD5", color:"#9A3412" },
  "MEDIUM": { bg:"#FEF9C3", color:"#854D0E" },
  "LOW":    { bg:"#DCFCE7", color:"#166534" },
};

const LabelDark = ({ txt }) => (
  <div className="text-[10px] text-[#94A3B8] font-semibold uppercase tracking-[.07em] mb-[5px]">{txt}</div>
);

const nameList = (v) => {
  if (!v) return "—";
  if (Array.isArray(v)) return v.map(i => typeof i==="string"?i:String(i)).join(", ") || "—";
  if (typeof v === "object") return "—";
  return String(v);
};

/* ══ LIGHTBOX ══ */
function Lightbox({ src, onClose }) {
  if (!src) return null;
  const download = () => {
    const a = document.createElement("a");
    a.href = src; a.download = src.split("/").pop() || "image";
    a.target = "_blank"; a.click();
  };
  return (
    <div onClick={onClose}
      className="fixed inset-0 z-[9999] bg-black/[.88] flex items-center justify-center flex-col gap-3">
      <div onClick={e=>e.stopPropagation()}
        className="flex items-center gap-3 px-4 py-2 bg-white/[.08] rounded-xl">
        <button onClick={download}
          className="flex items-center gap-1.5 px-[14px] py-[6px] bg-[rgb(124,132,207)] text-white border-none rounded-lg text-[13px] font-semibold cursor-pointer font-[inherit]">
          <Download size={13}/> Download
        </button>
        <button onClick={onClose}
          className="flex items-center gap-1 px-[14px] py-[6px] bg-[rgba(255,255,255,.12)] text-white border-none rounded-lg text-[13px] font-medium cursor-pointer font-[inherit]">
          <X size={13}/> Close
        </button>
      </div>
      <img src={src} alt="preview" onClick={e=>e.stopPropagation()}
        className="max-w-[92vw] max-h-[80vh] rounded-[10px] shadow-[0_8px_40px_rgba(0,0,0,.5)] object-contain"/>
    </div>
  );
}

/* ══ WORKSPACE CACHE ══ */
let _wsCache = null;
async function resolveWorkspaceName(productCode) {
  if (!productCode) return null;
  if (!_wsCache) {
    try {
      const res = await axiosInstance.get("/task_assignment/get_product_codes");
      const arr = Array.isArray(res.data?.product_codes)?res.data.product_codes
                : Array.isArray(res.data)?res.data:[];
      _wsCache = {};
      arr.forEach(item => {
        if (typeof item==="object") { const key=Object.keys(item)[0]; _wsCache[String(key)]=item[key]; }
      });
    } catch { _wsCache = {}; }
  }
  return _wsCache[String(productCode)] || null;
}

/* ══ MAIN ══ */
export default function TaskViewPanel({ taskId, onClose, isSubtaskView=false }) {
  const open = !!taskId;

  const [task,          setTask]          = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [activeTab,     setActiveTab]     = useState("desc");
  const [descExpanded,  setDescExpanded]  = useState(false);
  const [lightboxSrc,   setLightboxSrc]   = useState(null);
  const [workspaceName, setWorkspaceName] = useState(null);
  const [subDetailId,   setSubDetailId]   = useState(null);

  useEffect(() => {
    if (!taskId) { setTask(null); return; }
    setLoading(true); setError(null); setTask(null);
    setActiveTab("desc"); setDescExpanded(false);
    setLightboxSrc(null); setWorkspaceName(null); setSubDetailId(null);

    axiosInstance.get(`/task_assignment/get_task_details/${taskId}`)
      .then(async res => {
        if (res.data?.error) { setError(res.data.error); }
        else {
          setTask(res.data);
          if (!res.data.product_name && res.data.product_code) {
            const name = await resolveWorkspaceName(res.data.product_code);
            if (name) setWorkspaceName(name);
          }
        }
      })
      .catch(err => setError(err?.response?.data?.error||"Failed to load task"))
      .finally(() => setLoading(false));
  }, [taskId]);

  useEffect(() => {
    if (!task) return;
    setTimeout(() => {
      const c = document.getElementById("tvp-desc-display");
      if (!c) return;
      c.querySelectorAll("img").forEach(img => {
        img.style.cursor = "zoom-in";
        img.onclick = () => setLightboxSrc(img.src);
      });
    }, 100);
  }, [task, activeTab, descExpanded]);

  const sc        = task ? (STATUS_CFG[(task.task_status||"").toLowerCase()] || STATUS_CFG["open"]) : {};
  const pc        = task ? (PRIORITY_CFG[(task.priority||"").toUpperCase()] || PRIORITY_CFG["MEDIUM"]) : {};
  const isOverdue = task?.completion_sla && new Date(task.completion_sla) < new Date()
                    && !["completed","closed"].includes((task.task_status||"").toLowerCase());
  const notes    = Array.isArray(task?.notes)            ? task.notes            : [];
  const actLog   = Array.isArray(task?.activity_log)     ? task.activity_log     : [];
  const subtasks = Array.isArray(task?.subtasks)         ? task.subtasks         : [];
  const files    = Array.isArray(task?.download_urls)    ? task.download_urls    : [];
  const occDates = Array.isArray(task?.occurrence_dates) ? task.occurrence_dates : [];
  const isSubtask = !!(task?.parent_id);

  const MAIN_TABS = [
    { id:"desc",       label:"Description",  icon:<AlignLeft size={13}/>,     badge:null            },
    { id:"attach",     label:"Attachments",  icon:<Paperclip size={13}/>,     badge:files.length    },
    { id:"raci",       label:"RACI Matrix",  icon:<Users size={13}/>,          badge:null            },
    { id:"subtasks",   label:"Subtasks",     icon:<CheckSquare size={13}/>,   badge:subtasks.length },
    { id:"occurrence", label:"Occurrences",  icon:<Calendar size={13}/>,      badge:occDates.length },
    { id:"comments",   label:"Comments",     icon:<MessageSquare size={13}/>, badge:notes.length    },
    { id:"activity",   label:"Activity Log", icon:<Activity size={13}/>,      badge:actLog.length   },
  ];

  const SUBTASK_TABS = [
    { id:"desc",     label:"Description",  icon:<AlignLeft size={13}/>,     badge:null          },
    { id:"attach",   label:"Attachments",  icon:<Paperclip size={13}/>,     badge:files.length  },
    { id:"comments", label:"Comments",     icon:<MessageSquare size={13}/>, badge:notes.length  },
    { id:"activity", label:"Activity Log", icon:<Activity size={13}/>,      badge:actLog.length },
  ];

  const TABS = isSubtask ? SUBTASK_TABS : MAIN_TABS;

  return (
    <>
      {open && (
        <div
          onClick={onClose}
          className={`fixed inset-0 bg-[rgba(15,23,42,.25)] backdrop-blur-[8px] ${isSubtaskView ? "z-[1599]" : "z-[1502]"}`}
        />
      )}

      <Lightbox src={lightboxSrc} onClose={()=>setLightboxSrc(null)}/>

      <div
        onClick={e=>e.stopPropagation()}
        className={`fixed top-0 right-0 bottom-0 bg-[#F8FAFC] flex flex-col overflow-y-auto transition-transform duration-[350ms] ease-[cubic-bezier(.4,0,.2,1)] ${
          isSubtaskView ? "w-[min(820px,94vw)] z-[1600]" : "w-[min(1100px,97vw)] z-[1604]"
        } ${open ? "translate-x-0" : "translate-x-full"}`}
      >

        {loading && (
          <div className="flex items-center justify-center flex-1 flex-col gap-3 min-h-screen">
            <Loader size={28} color="rgb(124,132,207)"/>
            <span className="text-[13px] text-[#94A3B8]">Loading task details...</span>
          </div>
        )}

        {!loading && error && (
          <div className="flex items-center justify-center flex-1 flex-col gap-3 min-h-screen">
            <span className="text-[14px] text-[#EF4444]">{error}</span>
            <button onClick={onClose} className="text-[13px] text-[rgb(124,132,207)] bg-transparent border-none cursor-pointer">Close</button>
          </div>
        )}

        {!loading && !error && task && (
          <div className="flex flex-col min-h-full">

            {/* ══ HEADER ══ */}
            <div className="bg-[linear-gradient(135deg,rgb(124,132,207),rgb(187,196,255))] shrink-0 px-5 py-[14px]">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="text-[12px] text-white/75 mb-[6px] flex items-center gap-[6px]">
                    <button onClick={onClose}
                      className="flex items-center gap-1 bg-transparent border-none cursor-pointer text-white/75 text-[12px] font-[inherit]">
                      <ChevronLeft size={13}/>
                      {isSubtask ? "Subtasks" : "Tasks"}
                    </button>
                    <ChevronRight size={11}/>
                    <span className="text-white font-semibold">#{task.unique_seq_id}</span>
                    {isSubtask && (
                      <span className="bg-white/20 text-white text-[10px] px-2 py-px rounded-full font-medium">Subtask</span>
                    )}
                  </div>
                  <div className="text-[18px] font-semibold text-white leading-[1.3]">{task.task_type}</div>
                  {isSubtask && task.parent_id_name && (
                    <div className="text-[11px] text-white/70 mt-1">
                      Subtask of: <span className="text-white font-medium">{task.parent_id_name}</span>
                    </div>
                  )}
                </div>
                <button onClick={onClose}
                  className="w-7 h-7 rounded-lg bg-white/15 border border-white/30 flex items-center justify-center cursor-pointer ml-4 shrink-0">
                  <X size={14} color="#fff"/>
                </button>
              </div>
            </div>

            {/* ══ META ══ */}
            <div className="bg-white border-b-[0.5px] border-[#E8EAF0] px-[18px] py-[14px]">
              <div className="grid grid-cols-4 gap-[14px] pb-3 border-b-[0.5px] border-[#F1F5F9] mb-3">
                <div>
                  <LabelDark txt="Accountable"/>
                  <div className="text-[12px] text-[#1E293B] font-medium">{nameList(task.task_created_by_name||task.task_created_by)}</div>
                </div>
                <div>
                  <LabelDark txt="Responsible"/>
                  <div className="text-[12px] text-[#1E293B] font-medium">{nameList(task.assigned_to_name)}</div>
                </div>
                <div>
                  <LabelDark txt="Consulted"/>
                  {Array.isArray(task.observer_to_name) && task.observer_to_name.length > 0 ? (
                    <div className="flex flex-col gap-[3px]">
                      {task.observer_to_name.map((n,i) => (
                        <span key={i} className="bg-[#EEEDFE] text-[#3C3489] text-[10px] px-2 py-[2px] rounded-full border-[0.5px] border-[#AFA9EC] w-fit">{n}</span>
                      ))}
                    </div>
                  ) : <div className="text-[12px] text-[#1E293B]">{nameList(task.observer_to_name)}</div>}
                </div>
                <div>
                  <LabelDark txt="Informed"/>
                  {Array.isArray(task.informer_to_name) && task.informer_to_name.length > 0 ? (
                    <div className="flex flex-col gap-[3px]">
                      {task.informer_to_name.map((n,i) => (
                        <span key={i} className="bg-[#EEEDFE] text-[#3C3489] text-[10px] px-2 py-[2px] rounded-full border-[0.5px] border-[#AFA9EC] w-fit">{n}</span>
                      ))}
                    </div>
                  ) : <div className="text-[12px] text-[#1E293B]">{nameList(task.informer_to_name)}</div>}
                </div>
              </div>

              <div className="flex flex-wrap gap-[14px] items-start">
                <div>
                  <LabelDark txt="Status"/>
                  <span
                    className="inline-flex items-center gap-[5px] px-[10px] py-[3px] rounded-full text-[11px] font-semibold border"
                    style={{ background:sc.bg, color:sc.color, borderColor:(sc.dot||"#ccc")+"44" }}
                  >
                    <span className="w-[7px] h-[7px] rounded-full shrink-0" style={{ background:sc.dot }}/>{sc.label}
                  </span>
                </div>
                <div>
                  <LabelDark txt="Priority"/>
                  <span className="text-[11px] font-semibold px-[10px] py-[3px] rounded-full"
                    style={{ background:pc.bg, color:pc.color }}>
                    ⚑ {task.priority||"MEDIUM"}
                  </span>
                </div>
                <div>
                  <LabelDark txt="Workspace"/>
                  {(task.product_name||workspaceName)
                    ? <span className="bg-[#EAF3DE] text-[#27500A] text-[11px] font-semibold px-[10px] py-[3px] rounded-full border-[0.5px] border-[#97C459]">{task.product_name||workspaceName}</span>
                    : <span className="text-[12px] text-[#94A3B8]">—</span>}
                </div>
                <div>
                  <LabelDark txt="Start Date"/>
                  <div className={`text-[12px] ${task.start_date ? "text-[#1E293B]" : "text-[#94A3B8]"}`}>
                    {task.start_date ? fmtDate(task.start_date) : "Not specified"}
                  </div>
                </div>
                <div>
                  <LabelDark txt="Deadline"/>
                  <div className={`text-[12px] font-medium flex items-center gap-1 ${isOverdue ? "text-[#DC2626]" : "text-[#185FA5]"}`}>
                    {isOverdue && <AlertTriangle size={11} color="#DC2626"/>}
                    {fmtDate(task.completion_sla)}
                  </div>
                </div>
                {Array.isArray(task.functional_categories) && task.functional_categories.length > 0 && (
                  <div>
                    <LabelDark txt="Functional Categories"/>
                    <div className="flex flex-wrap gap-1">
                      {task.functional_categories.map((c,i) => (
                        <span key={i} className="bg-[#EEEDFE] text-[#3C3489] text-[10px] px-2 py-[2px] rounded-full border-[0.5px] border-[#AFA9EC]">{c}</span>
                      ))}
                    </div>
                  </div>
                )}
                {task.current_stage && (
                  <div>
                    <LabelDark txt="Stage"/>
                    <div className="text-[12px] text-[#1E293B]">{task.current_stage}</div>
                  </div>
                )}
              </div>
            </div>

            {/* ══ TABS ══ */}
            <div className="flex border-b-[0.5px] border-[#E8EAF0] bg-white overflow-x-auto shrink-0 [scrollbar-width:none] [-ms-overflow-style:none]">
              {TABS.map(tab => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-[6px] px-4 py-[10px] text-[12px] font-semibold cursor-pointer bg-transparent whitespace-nowrap font-[inherit] -mb-px border-b-2 ${
                      isActive ? "border-[rgb(124,132,207)] text-[rgb(124,132,207)]" : "border-transparent text-[#64748B]"
                    }`}
                  >
                    <span className={`shrink-0 ${isActive ? "text-[rgb(124,132,207)]" : "text-[#94A3B8]"}`}>{tab.icon}</span>
                    {tab.label}
                    {tab.badge !== null && (
                      <span className={`text-[10px] px-[6px] py-px rounded-full ${
                        isActive ? "bg-[#EEEDFE] text-[#3C3489]" : "bg-[#F1F5F9] text-[#64748B]"
                      }`}>
                        {tab.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* ══ TAB CONTENT ══ */}
            <div className="px-5 py-4 bg-white flex-1">

              {/* Description */}
              {activeTab==="desc" && (
                <div>
                  <div className="mb-[10px]">
                    <span className="text-[12px] text-[#94A3B8] font-semibold uppercase tracking-[.06em]">Content</span>
                  </div>
                  {(task.detailed_description||task.description) ? (
                    <>
                      <div id="tvp-desc-display"
                        className={`text-[13px] text-[#374151] leading-[1.7] overflow-hidden ${descExpanded ? "max-h-none" : "max-h-[220px]"}`}
                        dangerouslySetInnerHTML={{ __html:task.detailed_description||task.description }}/>
                      <button onClick={() => setDescExpanded(p=>!p)}
                        className="mt-2 text-[12px] text-[rgb(124,132,207)] bg-transparent border-none cursor-pointer font-semibold p-0">
                        {descExpanded ? "Show Less ▲" : "View More ▼"}
                      </button>
                    </>
                  ) : (
                    <p className="text-[13px] text-[#94A3B8] italic m-0">No description provided.</p>
                  )}
                </div>
              )}

              {/* Attachments */}
              {activeTab==="attach" && (
                <div>
                  {files.length===0 ? (
                    <div className="border-2 border-dashed border-[#D1D5DB] rounded-xl py-10 px-5 text-center bg-[#FAFBFE]">
                      <Paperclip size={28} color="#94A3B8" className="block mx-auto mb-[10px]"/>
                      <p className="text-[13px] text-[#64748B] m-0 font-medium">No attachments</p>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-3">
                      {files.map((f,fi) => (
                        <div key={fi} className="flex flex-col items-center gap-[6px] p-3 bg-[#FAFBFE] border-[0.5px] border-[#E8EAF0] rounded-[10px] w-[100px]">
                          <div className="w-[44px] h-[52px] bg-[#FEF2F2] border border-[#FECACA] rounded-lg flex items-center justify-center">
                            <FileText size={18} color="#EF4444"/>
                          </div>
                          <span className="text-[10px] text-[#64748B] text-center break-all leading-[1.3] max-w-full">{f.file_name}</span>
                          <a href={f.download_url} target="_blank" rel="noreferrer"
                            className="flex items-center justify-center w-6 h-6 rounded-[6px] bg-[#EEEDFE] text-[rgb(124,132,207)] no-underline">
                            <Download size={11}/>
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* RACI Matrix */}
              {activeTab==="raci" && !isSubtask && (
                <div className="overflow-x-auto rounded-lg border-[0.5px] border-[#E8EAF0]">
                  <table className="w-full border-collapse text-[12px]">
                    <thead>
                      <tr className="bg-[linear-gradient(135deg,rgb(124,132,207),rgb(187,196,255))]">
                        {["Task Action","R — Responsible","A — Accountable","C — Consulted","I — Informed"].map(h => (
                          <th
                            key={h}
                            className={`px-3 py-2 text-[11px] font-semibold text-white whitespace-nowrap ${h === "Task Action" ? "text-left" : "text-center"}`}
                          >{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(task.functional_categories) && task.functional_categories.length > 0 ? (
                        task.functional_categories.map((cat,ci) => (
                          <tr key={ci} className="border-t-[0.5px] border-[#F1F5F9]"
                            style={{ background:ci%2===0?"#fff":"#FAFBFE" }}>
                            <td className="p-[10px_12px]">
                              <span className="bg-[#EEEDFE] text-[#3C3489] text-[11px] px-2 py-[2px] rounded-full border-[0.5px] border-[#AFA9EC]">{cat}</span>
                            </td>
                            <td className="p-[10px_12px] text-center text-[#374151] text-[12px]">{nameList(task.assigned_to_name)}</td>
                            <td className="p-[10px_12px] text-center text-[#374151] text-[12px]">{nameList(task.task_created_by_name||task.task_created_by)}</td>
                            <td className="p-[10px_12px] text-center text-[#374151] text-[12px]">{nameList(task.observer_to_name)}</td>
                            <td className="p-[10px_12px] text-center text-[#374151] text-[12px]">{nameList(task.informer_to_name)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr className="border-t-[0.5px] border-[#F1F5F9]">
                          <td className="p-[10px_12px] text-[#94A3B8]">—</td>
                          <td className="p-[10px_12px] text-center text-[#374151] text-[12px]">{nameList(task.assigned_to_name)}</td>
                          <td className="p-[10px_12px] text-center text-[#374151] text-[12px]">{nameList(task.task_created_by_name||task.task_created_by)}</td>
                          <td className="p-[10px_12px] text-center text-[#374151] text-[12px]">{nameList(task.observer_to_name)}</td>
                          <td className="p-[10px_12px] text-center text-[#374151] text-[12px]">{nameList(task.informer_to_name)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Subtasks */}
              {activeTab==="subtasks" && !isSubtask && (
                <div className="overflow-x-auto rounded-lg border-[0.5px] border-[#E8EAF0]">
                  <table className="w-full border-collapse text-[12px]">
                    <thead>
                      <tr className="bg-[#F8FAFC]">
                        {["NAME","DEADLINE","ACCOUNTABLE","RESPONSIBLE","STATUS"].map(h => (
                          <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold text-[#94A3B8] tracking-[.06em]">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {subtasks.length===0 ? (
                        <tr><td colSpan={5} className="px-3 py-6 text-center text-[13px] text-[#94A3B8]">No subtasks found</td></tr>
                      ) : subtasks.map((s,si) => {
                        const ssc = STATUS_CFG[(s.task_status||"").toLowerCase()] || STATUS_CFG["open"];
                        return (
                          <tr key={si} className="border-t-[0.5px] border-[#F1F5F9] hover:bg-[#F8FAFC]">
                            <td className="p-[10px_12px]">
                              <span onClick={() => setSubDetailId(s._id || s)}
                                className="text-[12px] font-medium text-[#1d4ed8] cursor-pointer">
                                {s.task_type}
                              </span>
                            </td>
                            <td className="p-[10px_12px] text-[#374151] text-[12px]">{fmtDate(s.completion_sla)}</td>
                            <td className="p-[10px_12px] text-[#374151] text-[12px]">{s.task_created_by_name||"—"}</td>
                            <td className="p-[10px_12px] text-[#374151] text-[12px]">{nameList(s.assigned_to_name)}</td>
                            <td className="p-[10px_12px]">
                              <span className="text-[10px] font-semibold px-2 py-[2px] rounded-full"
                                style={{ background:ssc.bg, color:ssc.color }}>{ssc.label}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Occurrences */}
              {activeTab==="occurrence" && !isSubtask && (
                occDates.length===0 ? (
                  <p className="text-[13px] text-[#94A3B8] text-center py-6">No occurrence dates found</p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border-[0.5px] border-[#E8EAF0]">
                    <table className="w-full border-collapse text-[12px]">
                      <thead>
                        <tr className="bg-[#F8FAFC]">
                          {["#","Start Date","Deadline","Status"].map(h => (
                            <th key={h} className="px-3 py-[7px] text-left text-[11px] font-semibold text-[#94A3B8] tracking-[.06em]">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {occDates.map((occ,di) => {
                          const isObj     = typeof occ==="object" && occ!==null;
                          const startDate = isObj ? (occ.start_date||occ.start_date_utc||null) : occ;
                          const deadline  = isObj ? (occ.completion_sla||occ.completion_sla_utc||null) : null;
                          const status    = isObj ? (occ.status||"—") : "—";
                          const ssc       = STATUS_CFG[(status||"").toLowerCase()] || { bg:"#F1F5F9", color:"#475569" };
                          return (
                            <tr key={di} className="border-t-[0.5px] border-[#F1F5F9]"
                              style={{ background:di%2===0?"#fff":"#FAFBFE" }}>
                              <td className="px-3 py-2 text-[#94A3B8] text-[11px]">{di+1}</td>
                              <td className="px-3 py-2 text-[#374151] text-[12px]">{fmtDate(startDate)}</td>
                              <td className="px-3 py-2 text-[#374151] text-[12px]">{fmtDate(deadline)}</td>
                              <td className="px-3 py-2">
                                <span className="text-[10px] font-semibold px-2 py-[2px] rounded-full"
                                  style={{ background:ssc.bg, color:ssc.color }}>{status}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              )}

              {/* Comments */}
              {activeTab==="comments" && (
                <div>
                  {notes.length===0 ? (
                    <div className="text-center py-6 text-[#94A3B8] text-[13px] border-[0.5px] border-[#E8EAF0] rounded-lg">
                      <MessageSquare size={22} className="block mx-auto mb-2 opacity-40"/>
                      No comments yet
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      {notes.map((n,ni) => (
                        <div key={ni} className="flex gap-[10px]">
                          <Av name={n.user_name||"?"} size={30}/>
                          <div className="flex-1 bg-[#F8FAFC] rounded-[10px] px-[14px] py-[10px] border-[0.5px] border-[#E8EAF0]">
                            <div className="flex items-center justify-between mb-[6px]">
                              <span className="text-[12px] font-bold text-[#1E293B]">{n.user_name||"Unknown"}</span>
                              <span className="text-[11px] text-[#94A3B8]">{fmtDate(n.timestamp||n.date_time)}</span>
                            </div>
                            <div className="ql-editor !p-0 !text-[13px] !text-[#374151] !leading-[1.6] break-words"
                              dangerouslySetInnerHTML={{ __html: (n.comment||n.note||"")?.includes("<") ? (n.comment||n.note||"") : (n.comment||n.note||"")?.replace(/\n/g,"<br/>") }}/>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Activity Log */}
              {activeTab==="activity" && (
                <div className="flex flex-col gap-[10px]">
                  {actLog.length===0 ? (
                    <div className="text-center py-6 text-[#94A3B8] text-[13px]">No activity yet</div>
                  ) : [...actLog].reverse().map((log,li) => (
                    <div key={li} className="flex gap-[10px] items-start">
                      <div className="w-7 h-7 rounded-full bg-[#EEEDFE] flex items-center justify-center shrink-0">
                        <Activity size={12} color="rgb(124,132,207)"/>
                      </div>
                      <div className="flex-1">
                        {log.log_type && (
                          <span className="bg-[#F1F5F9] text-[#475569] text-[10px] font-semibold px-[7px] py-px rounded-full mb-1 inline-block">
                            {log.log_type}
                          </span>
                        )}
                        <p className="text-[13px] text-[#374151] my-[2px] leading-[1.5]">
                          {log.details||log.log_message||log.action||""}
                        </p>
                        <p className="text-[11px] text-[#94A3B8] m-0">
                          {log.log_user_name||""}
                          {log.assign_to && log.assign_to!=="--" ? ` → ${log.assign_to}` : ""}
                          {log.date_time ? ` · ${log.date_time}` : (log.log_time ? ` · ${fmtDate(log.log_time)}` : "")}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

            </div>
          </div>
        )}
      </div>

      {subDetailId && (
        <TaskViewPanel
          taskId={subDetailId}
          onClose={() => setSubDetailId(null)}
          isSubtaskView={true}
        />
      )}
    </>
  );
}